import React, { useEffect, useMemo, useState } from 'react'
import { customHeaders } from 'api/api'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { take } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useFeatureFlags, useLocalStorage } from '@node-space/hooks'
import { RestApi } from '@node-space/rest-api'
import {
  Box,
  Box as CalloutContainer,
  Box as WalletDescriptionWrapper,
} from '@node-space/storybook-components/dist/Box'
import { Button } from '@node-space/storybook-components/dist/Button'
import { Callout } from '@node-space/storybook-components/dist/Callout'
import { ImageIcon } from '@node-space/storybook-components/dist/ImageIcon'
import { Loader } from '@node-space/storybook-components/dist/Loader'
import { Text } from '@node-space/storybook-components/dist/Text'
import { PathNames } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useShowVerifiedBannerMutation } from 'hooks/mutations/useShowVerifiedBannerMutation'
import { useAccountPreferencesQuery } from 'hooks/queries/useAccountPreferencesQuery'
import { useMerchantsQuery } from 'hooks/queries/useMerchantsQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import {
  SearchWalletTable,
  searchWalletTableTags,
} from 'pages/Wallets/components/Table/SearchWalletTable'
import { MerchantType } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { setPaymentDescription } from 'utils/transactionText'
import GetStartedList from './components/getStarted/GetStartedList'
import GreetingUser from './components/getStarted/GreetingUser'

const Dashboard = () => {
  const { t } = useTranslation()
  const { enableAcl } = useFeatureFlags()

  const navigate = useNavigate()

  const { data: { merchantList = [] } = {} } = useMerchantsQuery()
  const { data: accountPreferences = {}, isLoading: isLoadingAccountPreferences } =
    useAccountPreferencesQuery()

  const { mutate: putShowVerifiedBanner } = useShowVerifiedBannerMutation()

  const [hideGetStarted, setHideGetStarted] = useLocalStorage('hideGetStarted')
  const { profile } = useProfileContext()
  const { enableSearchWalletTransactions } = useFeatureFlags()
  const [closedVerifiedBanner, setClosedVerifiedBanner] = useState(false)
  const [activeMerchant, setActiveMerchant] = useState<MerchantType>(merchantList?.[0])
  const [transactionsLoaded, setTransactionsLoaded] = useState(false)
  const [transactionsError, setTransactionsError] = useState(false)
  const [transactions, setTransactions] = useState([])
  const [transactionTableLoading, setTransactionTableLoading] = useState(false)

  const permissions = useMappedRoles()?.wallets

  const showVerifiedBanner = useMemo(
    () => !isLoadingAccountPreferences && accountPreferences?.showVerifiedBanner,
    [isLoadingAccountPreferences, accountPreferences]
  )

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.DASHBOARD_HOME, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  useEffect(() => {
    if (!!merchantList?.length) {
      setActiveMerchant(merchantList[0])
      getTransactions(merchantList[0])
    }
  }, [merchantList])

  const verifyWidgetCloseButton = () => {
    setClosedVerifiedBanner(true)
    putShowVerifiedBanner(false)
  }

  const getTransactions = async (merchant: MerchantType) => {
    setTransactionTableLoading(true)
    if (merchant.wallet) {
      try {
        const res = await RestApi.get(
          true,
          `transaction/${merchant.wallet.id}`,
          null,
          null,
          customHeaders
        )
        setTransactionsError(false)
        setTransactions(res.data)
        setTransactionsLoaded(true)
      } catch (err) {
        console.error(err)
        setTransactionsLoaded(true)
        setTransactionsError(true)
      }
      setTransactionTableLoading(false)
    }
  }

  const handleMerchantClick = merchantObject => {
    setActiveMerchant(merchantObject)
    getTransactions(merchantObject)
  }

  const canViewGetStarted = enableAcl ? permissions.canEdit : true

  // Table header classes
  const thClassName =
    'px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'

  // Table row classes
  const tdClassName = 'px-3 py-3 whitespace-nowrap align-middle text-sm text-gray-900'

  return (
    <>
      <GreetingUser firstName={profile?.firstName} className="pb-4" />

      {showVerifiedBanner && !closedVerifiedBanner && (
        <CalloutContainer paddingB={16}>
          <Callout
            onClose={verifyWidgetCloseButton}
            showCloseButton
            state="success"
            message={t('verification.successMessage')}
          />
        </CalloutContainer>
      )}

      {!hideGetStarted && canViewGetStarted && (
        <GetStartedList setHideGetStarted={setHideGetStarted} />
      )}

      {!!merchantList?.length && (
        <Box>
          <Box className="mb-8 relative w-full">
            <Text testid="home-page-header" tag="h2" size="3xl" weight="bold" className="lg:mx-0">
              {t('wallets.title')}
            </Text>
            <Box className="absolute top-0 right-0">
              <Button
                testid="view-wallets-btn"
                onClick={() => {
                  track.Hotjar.event('user_clicked_dashboard_view_all_wallets')
                  track.GA.event({
                    category: 'Dashboard',
                    action: 'click',
                    label: 'Button: View All Wallets',
                  })

                  navigate(`${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`)
                }}
              >
                {t('viewAllWallets')}
              </Button>
            </Box>
          </Box>

          {!enableSearchWalletTransactions && (
            <div className="grid gap-6 mb-6 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
              {transactionsLoaded && !merchantList?.length ? (
                <div className="empty">{t('noMerchantIdContactSupport')}</div>
              ) : !!merchantList?.length ? (
                take(merchantList, 4)?.map(merchantObject => {
                  return merchantObject.wallet ? (
                    <div
                      className={clsx(
                        'cursor-pointer flex items-center p-4 border-2 border-gray-200 rounded-lg shadow-sm  hover:bg-gray-25',
                        {
                          'bg-gray-25': merchantObject.merchantId == activeMerchant?.merchantId,
                          'bg-white': merchantObject.merchantId !== activeMerchant?.merchantId,
                        }
                      )}
                      key={merchantObject.id}
                      onClick={() => handleMerchantClick(merchantObject)}
                    >
                      <div className="p-3 mr-4 rounded-full">
                        <span className="h-8 w-8">
                          {merchantObject?.wallet?.currency?.code && (
                            <ImageIcon
                              width={32}
                              height={32}
                              src={`${
                                process.env.ICONS_URL
                              }${merchantObject.wallet.currency.code.toLowerCase()}.svg`}
                            />
                          )}
                        </span>
                      </div>
                      <div>
                        <p className="mb-2 text-sm font-semibold text-gray-900">
                          {merchantObject.displayName} ...
                          {merchantObject.merchantId.substr(-8, 8)}
                        </p>
                        <p className="text-sm font-normal text-gray-800">
                          {merchantObject.wallet.balance || null}
                          {merchantObject.wallet.currency.code}
                        </p>
                        <p className="mb-2 text-sm font-normal text-gray-400">
                          {merchantObject.wallet.description}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="empty" key={merchantObject.id}>
                      {t('merchantIdHasNoWallet')}
                      <Link to={`${PathNames.SETTINGS}${PathNames.MANAGE_MERCHANT_DETAILS}`}>
                        <strong>{t('fixThatHere')}</strong>
                      </Link>
                    </div>
                  )
                })
              ) : (
                <Loader theme="dark" size="small" />
              )}
            </div>
          )}
        </Box>
      )}

      {!enableSearchWalletTransactions &&
        !!merchantList?.length &&
        merchantList?.[0]?.wallet &&
        (transactionTableLoading ? (
          <Box flex justifyContent="center">
            <Loader theme="dark" size="large" />
          </Box>
        ) : (
          <div className="w-full shadow-lg rounded-md sm:rounded-lg border mb-8">
            <header className="px-5 py-4 border-b">
              <h4 className="font-semibold text-gray-800">{t('recentWalletTransactions')}</h4>
            </header>
            <div className="overflow-y-scroll">
              <table className="min-w-full divide-y divide-gray-200">
                {transactionsLoaded && transactions && transactions.length > 0 ? (
                  [
                    <thead key="thead" className="bg-gray-25">
                      <tr>
                        <th scope="col" className={thClassName}>
                          Currency
                        </th>
                        <th scope="col" className={thClassName}>
                          Date
                        </th>
                        <th scope="col" className={thClassName}>
                          Tran. ID
                        </th>
                        <th scope="col" className={thClassName}>
                          Description
                        </th>
                        <th scope="col" className={thClassName}>
                          Amount
                        </th>
                        <th scope="col" className={thClassName}>
                          Hash
                        </th>
                      </tr>
                    </thead>,
                    <tbody key="body">
                      {transactions.map(transaction => {
                        return (
                          <tr key={transaction.id} className="gray-0 bg-white even:bg-gray-0">
                            <td className={tdClassName}>{transaction.wallet.currency.code}</td>
                            <td className={tdClassName}>
                              {dayjs(transaction.dateCreated).format('DD/MM/YYYY, HH:mm:ss')}
                            </td>
                            <td className={tdClassName}>{transaction.id}</td>
                            <td className={tdClassName}>{setPaymentDescription(transaction)}</td>
                            <td className={tdClassName}>{transaction.amount}</td>
                            <td className={tdClassName}>
                              {transaction?.context?.hash
                                ? transaction.context.hash?.includes('offChain')
                                  ? t('offChain')
                                  : transaction.context.hash
                                : '-'}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>,
                  ]
                ) : transactionsLoaded ? (
                  <tbody>
                    <tr className="empty">
                      <td className="p-4">
                        {transactionsError ? 'An error occured!' : 'No wallet transactions'}
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    <tr className="empty">
                      <td className="flex justify-center">
                        <Loader theme="dark" size="medium" />
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
            </div>
          </div>
        ))}

      {enableSearchWalletTransactions && (
        <>
          <WalletDescriptionWrapper flex>
            <Text testid="recent-transactions-label">{t('recentWalletTransactions')}</Text>
            <Text
              size="xs"
              color="primary-500"
              className="ml-2 bg-purple-100 font-medium px-2.5 py-0.5 rounded"
            >
              {t('beta')}
            </Text>
          </WalletDescriptionWrapper>
          <SearchWalletTable pageId={searchWalletTableTags.DASHBOARD_RECENT_WALLETS.id} />
        </>
      )}
    </>
  )
}

export default Dashboard
